import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  createEngine,
  endpointAdapter,
  formatFromFilename,
  localOntologyAdapter,
  parseOntology,
  type Engine,
  type OntologyFormat,
  type Source,
} from "@nl2sparql/core";
import { makeAnthropicComplete } from "../llm/anthropic.ts";
import {
  deleteOntology,
  listOntologies,
  loadHistory,
  loadSettings,
  saveHistory,
  saveOntology,
  saveSettings,
  type HistoryEntry,
  type Settings,
} from "./persistence.ts";

interface EngineState {
  engine: Engine;
  sources: Source[];
  ready: boolean;
  history: HistoryEntry[];
  settings: Settings;
  llmEnabled: boolean;
  addOntology: (file: File) => Promise<void>;
  addEndpoint: (label: string, endpoint: string) => void;
  removeSource: (id: string) => Promise<void>;
  setAnthropicKey: (key: string | undefined) => Promise<void>;
  pushHistory: (entry: HistoryEntry) => void;
}

const EngineContext = createContext<EngineState | null>(null);

function slugId(prefix: string, name: string): string {
  return `${prefix}-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${Date.now().toString(36)}`;
}

export function PipelineProvider({ children }: { children: ReactNode }) {
  const engine = useMemo(() => createEngine({ userAgent: "nl2sparql-web/0.1 (kglabs)" }), []);
  const [sources, setSources] = useState<Source[]>(() => engine.listSources());
  const [ready, setReady] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [settings, setSettings] = useState<Settings>({});
  const [llmEnabled, setLlmEnabled] = useState(false);
  const loaded = useRef(false);

  const refreshSources = useCallback(() => setSources(engine.listSources()), [engine]);

  // One-time load of persisted ontologies, endpoints, settings, history.
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    (async () => {
      const stored = await listOntologies();
      for (const o of stored) {
        try {
          const onto = parseOntology(o.text, o.format as OntologyFormat);
          engine.addSource(localOntologyAdapter(o.id, o.name, onto));
        } catch {
          // skip an ontology that no longer parses
        }
      }
      const s = await loadSettings();
      for (const ep of s.endpoints ?? []) {
        engine.addSource(endpointAdapter(ep.id, ep.label, ep.endpoint));
      }
      if (s.anthropicKey) {
        engine.llmComplete = makeAnthropicComplete(s.anthropicKey);
        setLlmEnabled(true);
      }
      setSettings(s);
      setHistory(await loadHistory());
      refreshSources();
      setReady(true);
    })();
  }, [engine, refreshSources]);

  const addOntology = useCallback(
    async (file: File) => {
      const text = await file.text();
      const format = formatFromFilename(file.name);
      const name = file.name.replace(/\.[^.]+$/, "");
      const id = slugId("onto", name);
      const onto = parseOntology(text, format); // throws on bad RDF — surfaced to caller
      engine.addSource(localOntologyAdapter(id, name, onto));
      await saveOntology({ id, name, text, format, addedAt: Date.now() });
      refreshSources();
    },
    [engine, refreshSources],
  );

  const addEndpoint = useCallback(
    (label: string, endpoint: string) => {
      const id = slugId("ep", label || "endpoint");
      engine.addSource(endpointAdapter(id, label || endpoint, endpoint));
      const next: Settings = {
        ...settings,
        endpoints: [...(settings.endpoints ?? []), { id, label: label || endpoint, endpoint }],
      };
      setSettings(next);
      void saveSettings(next);
      refreshSources();
    },
    [engine, settings, refreshSources],
  );

  const removeSource = useCallback(
    async (id: string) => {
      if (id === "wikidata") return;
      engine.removeSource(id);
      await deleteOntology(id).catch(() => {});
      if (settings.endpoints?.some((e) => e.id === id)) {
        const next: Settings = { ...settings, endpoints: settings.endpoints.filter((e) => e.id !== id) };
        setSettings(next);
        await saveSettings(next);
      }
      refreshSources();
    },
    [engine, settings, refreshSources],
  );

  const setAnthropicKey = useCallback(
    async (key: string | undefined) => {
      if (key) {
        engine.llmComplete = makeAnthropicComplete(key);
        setLlmEnabled(true);
      } else {
        engine.llmComplete = undefined;
        setLlmEnabled(false);
      }
      const next: Settings = { ...settings };
      if (key) next.anthropicKey = key;
      else delete next.anthropicKey;
      setSettings(next);
      await saveSettings(next);
    },
    [engine, settings],
  );

  const pushHistory = useCallback((entry: HistoryEntry) => {
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, 50);
      void saveHistory(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      engine,
      sources,
      ready,
      history,
      settings,
      llmEnabled,
      addOntology,
      addEndpoint,
      removeSource,
      setAnthropicKey,
      pushHistory,
    }),
    [engine, sources, ready, history, settings, llmEnabled, addOntology, addEndpoint, removeSource, setAnthropicKey, pushHistory],
  );

  return <EngineContext.Provider value={value}>{children}</EngineContext.Provider>;
}

export function useEngine(): EngineState {
  const ctx = useContext(EngineContext);
  if (!ctx) throw new Error("useEngine must be used within a PipelineProvider");
  return ctx;
}
